import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/auth-context';
import { checkMFARequirementStrict } from '@/lib/auth-utils';
import { readLoginRedirect } from '@/lib/auth-redirect';

import { logAuthError } from './log-auth-error';

/** `checking` holds the loader; `form` means the sign-in surface may render. */
export type RestoredSessionEntry = 'checking' | 'form';

/** `pending` is the auth context still settling; the other two are the verdict taken once it has. */
type EntryState = 'pending' | RestoredSessionEntry;

const MFA_VERIFY_PATH = '/auth/mfa-verify';

/**
 * Admits a session the browser already holds when the sign-in route is opened
 * directly, through the same completion a fresh sign-in uses.
 *
 * Decided ONCE per mount, from the first settled auth state. A session that
 * appears later is a sign-in in progress (password and passkey both persist an
 * aal1 session before the step-up is answered), and the page's own handlers
 * own that - redirecting on it would pull the user out of the step-up view.
 *
 * Fails closed: a step-up that is owed goes to the existing step-up page, and a
 * step-up that cannot be read shows the form rather than admitting.
 */
export function useRestoredSessionEntry(
  completeSignIn: (userId: string, preferredPath?: string | null) => Promise<boolean>,
): RestoredSessionEntry {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [entry, setEntry] = useState<EntryState>('pending');
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  // Reset on mount, not only cleared on unmount: a StrictMode mount -> cleanup
  // -> mount reuses the ref, and one left `false` would discard the answer.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const admitRestoredSession = useEffectEvent(async (userId: string) => {
    const requirement = await checkMFARequirementStrict();
    if (!mountedRef.current) {
      return;
    }
    if (requirement.status === 'required') {
      await navigate(MFA_VERIFY_PATH, { replace: true });
      return;
    }
    if (requirement.status === 'indeterminate') {
      setEntry('form');
      return;
    }
    const proceeded = await completeSignIn(
      userId,
      readLoginRedirect(search, globalThis.location.origin),
    );
    // Stays on the loader once admitted: the navigation unmounts this page, and
    // showing the form in the gap would be the flash this hook exists to avoid.
    if (!proceeded && mountedRef.current) {
      setEntry('form');
    }
  });

  // Taken once, from the first settled auth state. Adjusting state during
  // render rather than in an effect keeps the signed-out case from painting a
  // loader frame before the form.
  if (entry === 'pending' && !loading) {
    setEntry(user ? 'checking' : 'form');
  }

  useEffect(() => {
    if (entry !== 'checking' || startedRef.current || !user) {
      return;
    }
    startedRef.current = true;
    admitRestoredSession(user.id).catch((error: unknown) => {
      logAuthError('restoredSessionEntry', error);
      if (mountedRef.current) {
        setEntry('form');
      }
    });
  }, [entry, user]);

  return entry === 'pending' ? 'checking' : entry;
}
